import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { testimonials, faqItems } from '@/mocks/comics';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const aboutTitle = '关于我们 - 汗汗漫画官网';
  const aboutDesc = '汗汗漫画官网致力于为漫画爱好者提供最优质的在线阅读体验。汇聚韩国漫画、日本漫画、国产漫画等海量资源，虫虫漫画免费漫画弹窗入口，资源互联互通。了解我们的品牌故事与口碑评价。';

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: aboutTitle,
    description: aboutDesc,
    url: 'https://hanhancomic.com/about',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title={aboutTitle}
        description={aboutDesc}
        keywords="汗汗漫画官网,关于汗汗漫画,漫画平台,韩国漫画大全,虫虫漫画入口"
        canonicalUrl="/about"
      />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Hero */}
          <section className="relative py-16 md:py-20 px-4 md:px-6 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20and%20artistic%20illustration%20of%20a%20cozy%20reading%20space%20with%20comic%20books%20scattered%20around%2C%20soft%20warm%20lighting%20through%20window%2C%20comfortable%20armchair%2C%20creative%20and%20inspiring%20atmosphere%2C%20warm%20amber%20and%20coral%20color%20palette%2C%20no%20text&width=1600&height=500&seq=about-hero-bg-01&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
            <div className="relative z-10 text-center max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-white mb-4">
                关于汗汗漫画
              </h1>
              <p className="text-white/85 text-sm md:text-base leading-relaxed">
                汗汗漫画官网，致力于为广大漫画爱好者提供最优质的在线阅读体验
              </p>
            </div>
          </section>

          <div className="px-4 md:px-6 py-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />

              {/* Brand Story */}
              <section className="mt-6 mb-16">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6">
                  品牌介绍
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      汗汗漫画成立于2020年，是一个专注于漫画在线阅读的综合平台。我们汇聚了韩国漫画、日本漫画、国产漫画等丰富资源，并与虫虫漫画等平台建立了深度合作关系，实现资源互联互通。
                    </p>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      作为国内领先的漫画阅读平台之一，汗汗漫画始终坚持以用户为中心，不断优化阅读体验。我们的内容团队每日精选优质漫画，确保读者能在第一时间看到最新、最热门的漫画作品。
                    </p>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      目前，汗汗漫画官网拥有超过百万注册用户，日均活跃读者数十万。我们设有虫虫漫画免费漫画弹窗入口，方便用户在不同漫画世界间自由切换，畅享阅读乐趣。
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-5 rounded-xl bg-background-100 text-center">
                      <span className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-book-open-line text-lg"></i>
                      </span>
                      <p className="text-2xl font-bold font-heading text-foreground-900">10w+</p>
                      <p className="text-xs text-foreground-500 mt-1">漫画资源</p>
                    </div>
                    <div className="p-5 rounded-xl bg-background-100 text-center">
                      <span className="w-10 h-10 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-user-line text-lg"></i>
                      </span>
                      <p className="text-2xl font-bold font-heading text-foreground-900">100w+</p>
                      <p className="text-xs text-foreground-500 mt-1">注册用户</p>
                    </div>
                    <div className="p-5 rounded-xl bg-background-100 text-center">
                      <span className="w-10 h-10 rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-thumb-up-line text-lg"></i>
                      </span>
                      <p className="text-2xl font-bold font-heading text-foreground-900">99.7%</p>
                      <p className="text-xs text-foreground-500 mt-1">好评率</p>
                    </div>
                    <div className="p-5 rounded-xl bg-background-100 text-center">
                      <span className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-global-line text-lg"></i>
                      </span>
                      <p className="text-2xl font-bold font-heading text-foreground-900">24h</p>
                      <p className="text-xs text-foreground-500 mt-1">急速更新</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Testimonials */}
              <section className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6">
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-600 mb-8">
                  听听我们的读者怎么说——来自漫画爱好者的真实评价
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {testimonials.map((t) => (
                    <div
                      key={t.id}
                      className="p-6 rounded-xl bg-background-100 border border-background-200/70"
                    >
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <span key={i} className="w-4 h-4 flex items-center justify-center text-accent-500">
                            <i className="ri-star-fill text-sm"></i>
                          </span>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                        "{t.content}"
                      </p>
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-bold">
                          {t.name.charAt(0)}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                          <p className="text-xs text-foreground-400">{t.date}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Contact */}
              <section className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6">
                  联系我们
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                    <h3 className="text-lg font-semibold text-foreground-900 mb-4">联系方式</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0">
                          <i className="ri-mail-line"></i>
                        </span>
                        <span className="text-sm text-foreground-700">contact@hanhancomic.com</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center flex-shrink-0">
                          <i className="ri-customer-service-line"></i>
                        </span>
                        <span className="text-sm text-foreground-700">在线客服：工作日 9:00 - 18:00</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-600 flex items-center justify-center flex-shrink-0">
                          <i className="ri-map-pin-line"></i>
                        </span>
                        <span className="text-sm text-foreground-700">中国 · 北京</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                    <h3 className="text-lg font-semibold text-foreground-900 mb-4">快速链接</h3>
                    <div className="space-y-3">
                      <a href="#" className="flex items-center gap-3 group cursor-pointer">
                        <span className="w-9 h-9 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                          <i className="ri-external-link-line"></i>
                        </span>
                        <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors">
                          虫虫漫画免费漫画弹窗入口
                        </span>
                      </a>
                      <a href="#" className="flex items-center gap-3 group cursor-pointer">
                        <span className="w-9 h-9 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center flex-shrink-0 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors">
                          <i className="ri-shield-check-line"></i>
                        </span>
                        <span className="text-sm text-foreground-700 group-hover:text-accent-600 transition-colors">
                          用户协议与隐私政策
                        </span>
                      </a>
                      <a href="#" className="flex items-center gap-3 group cursor-pointer">
                        <span className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-600 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary-500 group-hover:text-background-50 transition-colors">
                          <i className="ri-question-answer-line"></i>
                        </span>
                        <span className="text-sm text-foreground-700 group-hover:text-secondary-600 transition-colors">
                          帮助中心
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}